/**
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  CENTRAL MAP POOL CONFIGURATION — Single Source of Truth       ║
 * ║                                                                 ║
 * ║  To rotate the competitive map pool, ONLY change                ║
 * ║  the ACTIVE_MAP_POOL array below.                               ║
 * ║  Every page, component, and feature in the app derives          ║
 * ║  its map list from this file.                                   ║
 * ╚══════════════════════════════════════════════════════════════════╝
 */

export interface MapDefinition {
    /** Internal ID used in DB, API paths, and URL params (e.g. 'dust2', 'nuke') */
    id: string;
    /** Human-readable display name (e.g. 'Dust 2', 'Nuke') */
    name: string;
    /** Path to the map thumbnail/overview image */
    image: string;
    /** Path to the radar image used in overlays */
    radarImage: string;
    /** Brand color for charts, cards, and UI accents */
    color: string;
    /** Tailwind gradient classes for card backgrounds */
    gradient: string;
    /** Whether this map has a second vertical layer (e.g. Nuke upper/lower) */
    hasLowerLayer: boolean;
    /** The Z-axis coordinate threshold separating the upper and lower layers (if hasLowerLayer is true) */
    lowerLayerZThreshold?: number;
}

// ─── All maps we have ever supported ─────────────────────────────
// NEVER remove entries from here — rotated-out maps still need
// metadata for historical data (old demos, past match results, etc.)
export const ALL_MAPS: MapDefinition[] = [
    {
        id: 'dust2',
        name: 'Dust 2',
        image: '/maps/dust2.webp',
        radarImage: '/maps/de_dust2.webp',
        color: '#d97706',
        gradient: 'from-amber-500/20 to-orange-500/10',
        hasLowerLayer: false,
    },
    {
        id: 'mirage',
        name: 'Mirage',
        image: '/maps/mirage.webp',
        radarImage: '/maps/de_mirage.webp',
        color: '#eab308',
        gradient: 'from-yellow-500/20 to-amber-500/10',
        hasLowerLayer: false,
    },
    {
        id: 'inferno',
        name: 'Inferno',
        image: '/maps/inferno.webp',
        radarImage: '/maps/de_inferno.webp',
        color: '#f97316',
        gradient: 'from-orange-500/20 to-red-500/10',
        hasLowerLayer: false,
    },
    {
        id: 'nuke',
        name: 'Nuke',
        image: '/maps/nuke.webp',
        radarImage: '/maps/de_nuke.webp',
        color: '#22c55e',
        gradient: 'from-green-500/20 to-emerald-500/10',
        hasLowerLayer: true,
        lowerLayerZThreshold: -500,
    },
    {
        id: 'overpass',
        name: 'Overpass',
        image: '/maps/overpass.webp',
        radarImage: '/maps/de_overpass.webp',
        color: '#3b82f6',
        gradient: 'from-blue-500/20 to-indigo-500/10',
        hasLowerLayer: false,
    },
    {
        id: 'ancient',
        name: 'Ancient',
        image: '/maps/ancient.webp',
        radarImage: '/maps/de_ancient.webp',
        color: '#14b8a6',
        gradient: 'from-teal-500/20 to-cyan-500/10',
        hasLowerLayer: false,
    },
    {
        id: 'anubis',
        name: 'Anubis',
        image: '/maps/anubis.webp',
        radarImage: '/maps/de_anubis.webp',
        color: '#a855f7',
        gradient: 'from-purple-500/20 to-violet-500/10',
        hasLowerLayer: false,
    },
    {
        id: 'vertigo',
        name: 'Vertigo',
        image: '/maps/vertigo.webp?v=2',
        radarImage: '/maps/de_vertigo.webp?v=2',
        color: '#6366f1',
        gradient: 'from-indigo-500/20 to-blue-500/10',
        hasLowerLayer: true,
        lowerLayerZThreshold: 11700,
    },
    {
        id: 'train',
        name: 'Train',
        image: '/maps/train.webp?v=2',
        radarImage: '/maps/de_train.webp?v=2',
        color: '#ef4444',
        gradient: 'from-red-500/20 to-rose-500/10',
        hasLowerLayer: false,
    },
    {
        id: 'cache',
        name: 'Cache',
        image: '/maps/cache.webp',
        radarImage: '/maps/de_cache.webp?v=cache-tone-3',
        color: '#84cc16',
        gradient: 'from-lime-500/20 to-green-500/10',
        hasLowerLayer: false,
    },
];

// ╔══════════════════════════════════════════════════════════════════╗
// ║  ⚡ ACTIVE MAP POOL — Change ONLY this when maps rotate ⚡     ║
// ╚══════════════════════════════════════════════════════════════════╝
// Default fallback pool
const DEFAULT_ACTIVE_POOL: string[] = [
    'dust2',
    'mirage',
    'inferno',
    'nuke',
    'overpass',
    'ancient',
    'vertigo',
];

export const ACTIVE_MAP_POOL: string[] = [...DEFAULT_ACTIVE_POOL];

// ─── Derived exports ─────────────────────────────────────────────

/** The currently active maps as full MapDefinition objects */
export const ACTIVE_MAPS: MapDefinition[] = [...DEFAULT_ACTIVE_POOL]
    .map(id => ALL_MAPS.find(m => m.id === id))
    .filter((m): m is MapDefinition => !!m);

/** Update the active map pool at runtime (called during app startup) */
export function setActiveMapPool(pool: string[]) {
    if (!pool || pool.length === 0) return;
    
    // Clear and mutate existing arrays so imported bindings reference the new data
    ACTIVE_MAP_POOL.length = 0;
    ACTIVE_MAP_POOL.push(...pool);

    ACTIVE_MAPS.length = 0;
    const newActiveMaps = ACTIVE_MAP_POOL
        .map(id => ALL_MAPS.find(m => m.id === id))
        .filter((m): m is MapDefinition => !!m);
    ACTIVE_MAPS.push(...newActiveMaps);
}

/** All known map IDs (for broad matching of historical data, Faceit results, etc.) */
export const ALL_MAP_IDS: string[] = ALL_MAPS.map(m => m.id);

/** Look up a map by id (searches ALL maps, not just active pool) */
export function getMapById(id: string | undefined | null): MapDefinition | undefined {
    if (!id) return undefined;
    const normalized = id.toLowerCase().replace(/^de_/, '').replace(/\s+/g, '');
    return ALL_MAPS.find(m => m.id === normalized);
}

/** Get the display name for a map id, with a fallback */
export function getMapName(id: string | undefined | null): string {
    return getMapById(id)?.name || id || 'Unknown';
}

/** Check if a map id belongs to the active competitive pool */
export function isActiveMap(id: string | undefined | null): boolean {
    if (!id) return false;
    const normalized = id.toLowerCase().replace(/^de_/, '').replace(/\s+/g, '');
    return ACTIVE_MAP_POOL.includes(normalized);
}
